import { useState, type FormEvent } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import LabBrand from "../components/LabBrand";
import { ArrowRight, Lock } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Login() {
  const { refresh, user, loading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = (loc.state as { from?: string } | null)?.from ?? "/";

  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.login(pin);
      await refresh();
      nav(from, { replace: true });
    } catch (error) {
      setError(error instanceof Error ? error.message : "Could not open the lab. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="flex min-h-screen items-center justify-center text-slate-500">Opening the lab…</div>;
  if (user) return <Navigate to={from} replace />;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#f7f8f5] px-5 py-10">
      <div className="mb-10"><LabBrand /></div>
      <form
        onSubmit={onSubmit}
        className="card w-full max-w-md space-y-6 p-8 sm:p-10"
      >
        <div>
          <p className="eyebrow mb-3 text-brand-700">WELCOME TO THE LAB</p>
          <h1 className="text-2xl font-semibold tracking-tight text-brand-900">Enter the lab PIN</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-500">
            One PIN to open the anatomy library and teaching studio.
          </p>
        </div>

        <label className="block">
          <span className="label-text">PIN</span>
          <div className="relative">
            <Lock
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="password"
              className="input pl-9 text-lg tracking-[0.3em]"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              inputMode="numeric"
              pattern="[0-9]{4,12}"
              minLength={4}
              maxLength={12}
              autoFocus
              aria-describedby={error ? "pin-error" : undefined}
              aria-invalid={Boolean(error)}
              required
              autoComplete="current-password"
            />
          </div>
        </label>

        {error && (
          <div id="pin-error" role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-200">
            {error}
          </div>
        )}

        <button type="submit" className="btn-primary w-full" disabled={busy}>
          {busy ? "Opening…" : "Enter lab"} {!busy && <ArrowRight size={15} />}
        </button>
      </form>

      <p className="mt-6 text-xs text-slate-400">Use the PIN shared by your teaching team.</p>
    </div>
  );
}
